import {
  applySessionEvent, applySessionSync, emptySessionState, reduceSessionState,
  type RevisionedSessionState, type SessionCommandType, type SessionEvent, type SessionState, type SessionSync,
} from "@calega/realtime";

export interface PendingCommand {
  commandId: string;
  expectedRevision: number;
  type: SessionCommandType;
  payload: unknown;
}
export type CommandResponse = { ok: true; event: SessionEvent } | { ok: false; error: { code?: string; status?: number; message: string } };

function sameInputTask(leftPayload: unknown, rightPayload: unknown) {
  type Input = { exerciseType?: string; pageId?: string; runId?: string; state?: { runId?: string; index?: unknown; phase?: unknown; stage?: unknown } | null };
  const left = leftPayload as Input | null;
  const right = rightPayload as Input | null;
  const runId = left?.runId ?? left?.state?.runId;
  return Boolean(runId && left?.state && right?.state && left.exerciseType === right.exerciseType
    && left.pageId === right.pageId && runId === (right.runId ?? right.state.runId)
    && left.state.index === right.state.index && left.state.phase === right.state.phase && left.state.stage === right.state.stage);
}

export function createCommandDelivery(callbacks: {
  send(command: PendingCommand, complete: (error: Error | null, response?: CommandResponse) => void): void;
  recover(delay?: number): void;
  change(current: RevisionedSessionState, displayed: SessionState, pending: number): void;
  notice(message: string): void;
}) {
  let current: RevisionedSessionState = { revision: 0, state: emptySessionState() };
  const pending: Array<PendingCommand & { sent: boolean }> = [];
  let active: (typeof pending)[number] | null = null;
  let connected = false;
  let disposed = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastInputSent = 0;
  let generation = 0;
  let hadConflict = false;
  let suspended = false;

  function displayed() {
    return pending.reduce((state, command) => {
      const payload = command.payload as { exerciseType?: string; state?: { phase?: string } | null } | null;
      // Timed presentations must use the server's canonical start time.
      if (payload?.exerciseType === "blitzlesen" && payload.state?.phase === "showing") return state;
      return reduceSessionState(state, command.type, command.payload);
    }, current.state);
  }

  function change() {
    callbacks.change(current, structuredClone(displayed()), pending.length);
  }

  function cancelTimer() {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  }

  function conflict(message: string) {
    hadConflict = true;
    pending.length = 0;
    active = null;
    connected = false;
    generation += 1;
    cancelTimer();
    callbacks.notice(message);
    change();
    callbacks.recover();
  }

  function accepted(event: SessionEvent) {
    if (event.commandId && pending[0]?.commandId === event.commandId) {
      pending.shift();
      active = null;
    }
  }

  function drain() {
    if (disposed || suspended || !connected || active || timer !== null || !pending.length) return;
    const command = pending[0]!;
    // Retry an ambiguous, already-sent command unchanged; the server checks its receipt first.
    if (command.expectedRevision > current.revision) return;
    const wait = command.type === "exercise.input" && !pending.some((entry) => entry.type !== "exercise.input")
      ? 200 - (Date.now() - lastInputSent) : 0;
    if (wait > 0) {
      timer = setTimeout(() => { timer = null; drain(); }, wait);
      return;
    }
    active = command;
    command.sent = true;
    if (command.type === "exercise.input") lastInputSent = Date.now();
    const sentGeneration = generation;
    const { sent: _sent, ...wireCommand } = command;
    callbacks.send(wireCommand, (error, response) => {
      if (disposed || sentGeneration !== generation || pending[0] !== command) return;
      active = null;
      if (response && !response.ok && response.error.code === "exercise_paused") {
        suspended = true; cancelTimer();
        callbacks.notice("Eine Eingabe wartet bis zum Fortsetzen der Pause.");
        change();
        return;
      }
      if (error || !response || !response.ok && (response.error.status === 429 || (response.error.status ?? 0) >= 500)) {
        connected = false;
        callbacks.notice("Eine Änderung wartet auf Bestätigung. Die Sitzung wird abgeglichen.");
        // The recovery coordinator also enforces this pause for focus/online events.
        callbacks.recover(!response?.ok && response?.error.status === 429 ? 12_000 : 1_000);
        return;
      }
      if (!response.ok) return conflict(response.error.message);
      accepted(response.event);
      current = applySessionEvent(current, response.event);
      if (pending.length && pending[0]!.expectedRevision !== current.revision) {
        return conflict("Die andere Seite hat die Sitzung geändert. Bitte den aktuellen Stand prüfen und die Aktion bei Bedarf erneut ausführen.");
      }
      change();
      if (!pending.length && !hadConflict) callbacks.notice("");
      drain();
    });
  }

  return {
    displayed,
    suspend(value: boolean) { suspended = value; if (value) cancelTimer(); else drain(); },
    enqueue(type: SessionCommandType, payload: unknown) {
      if (disposed) return;
      hadConflict = false;
      callbacks.notice("");
      const last = pending.at(-1);
      if (type === "exercise.input" && last?.type === type && !last.sent && sameInputTask(last.payload, payload)) {
        last.payload = payload;
      } else {
        if (pending.length >= 64) {
          callbacks.notice("Zu viele offene Änderungen. Bitte auf die Synchronisierung warten.");
          change();
          return;
        }
        pending.push({ commandId: crypto.randomUUID(), expectedRevision: last ? last.expectedRevision + 1 : current.revision, type, payload, sent: false });
      }
      if (type !== "exercise.input" && connected && !active) cancelTimer();
      change();
      drain();
    },
    event(event: SessionEvent) {
      if (disposed) return;
      if (event.revision > current.revision + 1) {
        connected = false;
        callbacks.recover();
        return;
      }
      const own = Boolean(event.commandId && pending[0]?.commandId === event.commandId);
      accepted(event);
      const previousRevision = current.revision;
      current = applySessionEvent(current, event);
      if (!own && current.revision > previousRevision && pending.length) {
        // Resolve a sent request by ID, but never replay its dependent queued actions on a foreign state.
        if (pending[0]?.sent) {
          pending.splice(1);
          hadConflict = true;
          callbacks.notice("Die andere Seite hat die Sitzung geändert. Bitte die offenen Aktionen nach dem Abgleich prüfen.");
        }
        else return conflict("Die andere Seite hat die Sitzung geändert. Bitte die offene Aktion erneut prüfen.");
      }
      change();
      if (own && !pending.length && !hadConflict) callbacks.notice("");
      drain();
    },
    sync(sync: SessionSync) {
      if (disposed) return;
      for (const event of sync.events) accepted(event);
      current = applySessionSync(current, sync);
      if (pending.length && pending[0]!.expectedRevision + 1 < current.revision) {
        pending.splice(1);
        hadConflict = true;
        callbacks.notice("Die Sitzung wurde inzwischen geändert. Bitte die offenen Aktionen nach dem Abgleich prüfen.");
      }
      change();
    },
    connection(value: boolean) {
      if (disposed) return;
      connected = value;
      if (!value) {
        generation += 1;
        active = null;
        cancelTimer();
      }
      drain();
    },
    dispose() {
      disposed = true;
      generation += 1;
      cancelTimer();
      pending.length = 0;
    },
  };
}
